import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getLiveEvents,
  getCalendarMonth,
  getMyRegistrations,
} from "../services/liveEventsService";

export const fetchLiveEventsThunk = createAsyncThunk(
  "liveEvents/fetchLiveEvents",
  async ({ filter = "next30days", month, year, category, silent = false } = {}, { rejectWithValue }) => {
    try {
      const data = await getLiveEvents({ filter, month, year, category });
      return { data, filter, month, year, category, silent };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch live events");
    }
  }
);

export const fetchCalendarMonthThunk = createAsyncThunk(
  "liveEvents/fetchCalendarMonth",
  async ({ month, year }, { rejectWithValue }) => {
    try {
      const data = await getCalendarMonth(month, year);
      return { data, month, year };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch calendar data");
    }
  }
);

export const fetchMyRegistrationsThunk = createAsyncThunk(
  "liveEvents/fetchMyRegistrations",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getMyRegistrations();
      return data;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch registrations");
    }
  }
);

const initialState = {
  events: [],
  activeFilter: "next30days",
  selectedMonth: new Date().getMonth(),
  selectedYear: new Date().getFullYear(),
  monthCalendar: { events: [], offset: 0, days: 0 },
  myRegistrations: [],
  loading: false,
  isRevalidating: false,
  initialLoaded: false,
  lastFetched: null,
  error: null,
};

const liveEventsSlice = createSlice({
  name: "liveEvents",
  initialState,
  reducers: {
    setLiveFilter(state, action) {
      const { filter, month, year } = action.payload || {};
      if (filter !== undefined) state.activeFilter = filter;
      if (month !== undefined) state.selectedMonth = month;
      if (year !== undefined) state.selectedYear = year;
    },
    updateEventRegistration(state, action) {
      const { eventId, isRegistered } = action.payload || {};
      const numId = Number(eventId);
      
      // Update in main events array
      state.events = state.events.map((ev) => {
        if (Number(ev.id || ev.event_id) === numId) {
          const currentCount = ev.filled_count || 0;
          return {
            ...ev,
            is_registered: isRegistered,
            filled_count: isRegistered ? currentCount + 1 : Math.max(0, currentCount - 1),
          };
        }
        return ev;
      });

      // Update in calendar events if present
      if (state.monthCalendar?.events) {
        state.monthCalendar.events = state.monthCalendar.events.map((ev) => {
          if (Number(ev.id || ev.event_id) === numId) {
            return { ...ev, is_registered: isRegistered };
          }
          return ev;
        });
      }
    },
    resetLiveEvents(state) {
      state.events = [];
      state.loading = false;
      state.isRevalidating = false;
      state.initialLoaded = false;
      state.lastFetched = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Live Events
      .addCase(fetchLiveEventsThunk.pending, (state, action) => {
        const isSilent = Boolean(action.meta.arg?.silent);
        if (isSilent) {
          state.isRevalidating = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchLiveEventsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isRevalidating = false;
        state.initialLoaded = true;
        state.events = action.payload.data;
        state.activeFilter = action.payload.filter;
        if (action.payload.month !== undefined) {
          state.selectedMonth = action.payload.month - 1;
        }
        if (action.payload.year !== undefined) {
          state.selectedYear = action.payload.year;
        }
        state.lastFetched = Date.now();
      })
      .addCase(fetchLiveEventsThunk.rejected, (state, action) => {
        state.loading = false;
        state.isRevalidating = false;
        state.error = action.payload || "Failed to load events";
      })

      // Fetch Calendar Month
      .addCase(fetchCalendarMonthThunk.fulfilled, (state, action) => {
        state.monthCalendar = action.payload.data;
      })

      // Fetch My Registrations
      .addCase(fetchMyRegistrationsThunk.fulfilled, (state, action) => {
        state.myRegistrations = action.payload;
      });
  },
});

export const {
  setLiveFilter,
  updateEventRegistration,
  resetLiveEvents,
} = liveEventsSlice.actions;

export default liveEventsSlice.reducer;

export const selectLiveEvents = (state) => state.liveEvents.events;
export const selectLiveEventsLoading = (state) => state.liveEvents.loading;
export const selectLiveEventsIsRevalidating = (state) => state.liveEvents.isRevalidating;
export const selectLiveEventsFilter = (state) => state.liveEvents.activeFilter;
export const selectLiveEventsMonth = (state) => state.liveEvents.selectedMonth;
export const selectLiveEventsYear = (state) => state.liveEvents.selectedYear;
export const selectLiveEventsInitialLoaded = (state) => state.liveEvents.initialLoaded;
export const selectLiveEventsLastFetched = (state) => state.liveEvents.lastFetched;
